/**
 * Layout helpers for IntakeForm's responsive field grid.
 *
 * The intake grid is SIX columns on `sm+` screens (one column on mobile, where every
 * field stacks full width). A field occupies a share of those six columns based on its
 * width:
 *
 *   full  → 6 columns (a whole row)
 *   half  → 3 columns (two per row) — the default
 *   third → 2 columns (three per row) — e.g. City / State / ZIP on one line
 *
 * A field can request a specific width with `field.width` ('full' | 'half' | 'third').
 * For backwards compatibility, a field with no `width` falls back to the legacy
 * full-vs-half signal (`fullWidth` / `colSpan: 2`), and `notice` / `attachments` blocks
 * are always full width.
 *
 * `newRow` forces a field to begin a fresh row while keeping its own width, done with
 * `col-start-1`: CSS Grid cannot place an item in column 1 of a row whose column 1 is
 * already filled, so the item moves to the next row. On mobile (single column) the class
 * is inert.
 *
 * Everything here is pure, so it is unit-tested in isolation (see fieldGrid.test.js).
 */

const FULL_WIDTH_TYPES = new Set(['attachments', 'notice']);

/** The number of grid columns the intake field grid uses on `sm+`. */
export const GRID_COLUMNS = 6;

/** The admin-selectable widths, in the order the builder should present them. */
export const FIELD_WIDTHS = [
  ['full', 'Full width'],
  ['half', 'Half width'],
  ['third', 'Third width'],
];

/**
 * `fullWidth` is the admin-editable counterpart to the schema's legacy colSpan: 2.
 * Its explicit false value must be honored so an administrator can narrow a field
 * that code originally shipped at full width.
 */
export function isFieldFullWidth(field) {
  return field?.fullWidth === true || (field?.fullWidth === undefined && field?.colSpan === 2);
}

/**
 * The effective width bucket for a field: 'full' | 'half' | 'third'.
 * An explicit `field.width` wins; otherwise notices/attachments are full, and the legacy
 * fullWidth/colSpan signal decides between full and the half-width default.
 */
export function fieldWidth(field) {
  if (FULL_WIDTH_TYPES.has(field?.type)) return 'full';
  const width = field?.width;
  if (width === 'full' || width === 'half' || width === 'third') return width;
  return isFieldFullWidth(field) ? 'full' : 'half';
}

const WIDTH_SPAN_CLASS = {
  full: 'sm:col-span-6',
  half: 'sm:col-span-3',
  third: 'sm:col-span-2',
};

/** The Tailwind column-span class for a field's width in the six-column grid. */
export function fieldColSpanClass(field) {
  return WIDTH_SPAN_CLASS[fieldWidth(field)] || WIDTH_SPAN_CLASS.half;
}

/**
 * The complete grid classes for a field's wrapper: its column span, plus `sm:col-start-1`
 * when the field asked to begin a new row. Used by every intake grid child (regular
 * fields, notices and attachment blocks) so width and row-breaking behave identically
 * however the field is rendered.
 */
export function fieldGridClasses(field) {
  const span = fieldColSpanClass(field);
  return field?.newRow === true ? `${span} sm:col-start-1` : span;
}

/** True when a field fills a whole grid row (full width, or a notice/attachments block). */
export function fieldEndsGridRow(field) {
  return fieldWidth(field) === 'full';
}
